import React, { useContext } from 'react'
import './footer.scss'
import { useLocation, useNavigate } from 'react-router-dom'
import Link from '../LanguageSwitcher/LocaleLink'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext'
import { PROFILES, CONTACT } from '../../config/site'
import { openCookieSettings } from '../../lib/consent'
import allServices from '../../data/services'
import { localizeServices } from '../../data/localizeService'
import { LOCALIZED_LANGS, isLocalized, localizedPath, splitLocale } from '../../config/i18nRoutes'

const LANGUAGE_NAMES = { en: 'English', de: 'Deutsch', fr: 'Français', es: 'Español' }

// The services most buyers look for first; the full list is one click away.
const FOOTER_SERVICES = ['ai-development', 'generative-ai', 'rag', 'web-development', 'web-design', 'seo']

const Footer = () => {
  const navigate = useNavigate()
  const { translations: t, selectedLanguage, changeLanguage } = useContext(LanguageContext)
  const { base } = splitLocale(useLocation().pathname)

  // Real links to this page's other language URLs. The header switcher is a
  // script-only dropdown, so without these nothing a crawler can follow led
  // to /de, /fr or /es. Clicking goes through changeLanguage, which moves the
  // URL as well (see LocaleUrlSync).
  const languageLinks = isLocalized(base)
    ? ['en', ...LOCALIZED_LANGS].map((code) => ({ code, href: localizedPath(base, code) }))
    : []

  const services = localizeServices(allServices, t).filter((service) => FOOTER_SERVICES.includes(service.slug))
    .sort((a, b) => FOOTER_SERVICES.indexOf(a.slug) - FOOTER_SERVICES.indexOf(b.slug))

  const company = [
    { to: '/projects', label: t.projects },
    { to: '/industries', label: t.navIndustries || 'Industries' },
    { to: '/process', label: t.procNavLabel },
    { to: '/about-us', label: t.about },
    { to: '/articles', label: t.Blog },
    { to: '/contact-us', label: t.contact },
  ]

  const tools = [
    { to: '/estimate', label: t.estNavLabel },
    { to: '/ai-readiness', label: t.readyNavLabel },
    { to: '/seo-check', label: t.seocNavLabel },
    { to: '/accessibility-check', label: t.a11yNavLabel },
    { to: '/ai-savings', label: t.aisNavLabel },
  ]

  return (
    <footer className='footer'>
      <div className='footer-cta-row'>
        <div className='footer-cta-left'>
          <h3 className='footer-cta-title'>{t.contact11}</h3>
          <p className='footer-cta-sub'>{t.contact12}</p>
        </div>
        <button className='footer-cta-btn' onClick={() => navigate('/contact-us')}>
          {t.contact} <span className='footer-cta-btn-arrow'>→</span>
        </button>
      </div>

      <div className='footer-content'>
        <div className='footer-col footer-brand'>
          <p className='footer-brand-name'>MLMind</p>
          <p className='footer-brand-text'>{t.footerTagline}</p>
          <a className='footer-contact' href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
          <a className='footer-contact' href={`tel:${CONTACT.phone}`}>{CONTACT.phoneDisplay}</a>
          <p className='footer-contact footer-contact-plain'>{t.footerCity}</p>
        </div>

        <div className='footer-col'>
          <h4 className='footer-title'>{t.navServices}</h4>
          {services.map((service) => (
            <Link key={service.slug} className='footer-link' to={`/services/${service.slug}`}>{service.name}</Link>
          ))}
          <Link className='footer-link footer-link-more' to='/services'>{t.navAllServices || 'All services'} →</Link>
        </div>

        <div className='footer-col'>
          <h4 className='footer-title'>{t.footerCompany}</h4>
          {company.map((item) => (
            <Link key={item.to} className='footer-link' to={item.to}>{item.label}</Link>
          ))}
        </div>

        <div className='footer-col'>
          <h4 className='footer-title'>{t.footerTools}</h4>
          {tools.map((item) => (
            <Link key={item.to} className='footer-link' to={item.to}>{item.label}</Link>
          ))}
          <h4 className='footer-title footer-title-spaced'>{t.footerFollow}</h4>
          {/* Only profiles that exist — see PROFILES in config/site.js */}
          {PROFILES.filter((profile) => profile.url).map((profile) => (
            <a key={profile.name} className='footer-link footer-social-link' target='_blank' rel='noreferrer' href={profile.url}>
              {profile.name} <span className='footer-social-arrow'>↗</span>
            </a>
          ))}
        </div>
      </div>

      <div className='footer-bottom-bottom'>
        <div className='footer-bottom-bottom-left-group'>
          <p className='footer-bottom-bottom-left'>© {new Date().getFullYear()} MLMind</p>
          <Link className='footer-legal-link' to='/privacy'>{t.privNavLabel}</Link>
          <button type='button' className='footer-legal-link' onClick={openCookieSettings}>{t.ckSettingsLabel}</button>
        </div>
        {languageLinks.length > 0 && (
          <nav className='footer-languages' aria-label='Language'>
            {languageLinks.map(({ code, href }) => (
              <a
                key={code}
                className={`footer-legal-link${code === selectedLanguage ? ' footer-language-active' : ''}`}
                href={href}
                hrefLang={code}
                lang={code}
                onClick={(event) => {
                  event.preventDefault()
                  changeLanguage(code)
                }}
              >
                {LANGUAGE_NAMES[code]}
              </a>
            ))}
          </nav>
        )}

      </div>
    </footer>
  )
}

export default Footer
